/**
 * GlobalSearch — header command box (⌘K) wired to GET /api/v1/search.
 * Ported/adapted from school-transport-platform 2026-06-08 for zur-ops:
 * react-router navigation, `api` helper, teal theme, Polish UI.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Loader2, CornerDownLeft, ArrowDownUp, AlertCircle,
  AlertTriangle, Bus, UserCheck, FileText, ClipboardList, BookText, Users, Hash,
} from 'lucide-react';
import { api } from '@/shared/lib/api';

interface SearchItem { type: string; id: string; title: string; subtitle?: string; url: string }
interface SearchGroup { type: string; label: string; items: SearchItem[] }

const TYPE_VISUAL: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
  incident:   { icon: AlertTriangle, color: '#ea580c', bg: '#ffedd5' },
  vehicle:    { icon: Bus,           color: '#b45309', bg: '#fef3c7' },
  driver:     { icon: UserCheck,     color: '#2563eb', bg: '#dbeafe' },
  form:       { icon: FileText,      color: '#0d9488', bg: '#ccfbf1' },
  submission: { icon: ClipboardList, color: '#7c3aed', bg: '#ede9fe' },
  duty:       { icon: BookText,      color: '#0369a1', bg: '#e0f2fe' },
  task:       { icon: Hash,          color: '#8b5cf6', bg: '#ede9fe' },
  user:       { icon: Users,         color: '#db2777', bg: '#fce7f3' },
};
const FALLBACK_VISUAL = { icon: Search, color: '#64748b', bg: '#f1f5f9' };

export function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState<SearchGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const reqIdRef = useRef(0);

  const flatItems = useMemo(() => groups.flatMap(g => g.items), [groups]);

  // ⌘K / Ctrl-K focuses the box (app-wide)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        setOpen(true);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // close on outside click
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // debounced fetch via api helper (unwraps {success,data})
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setGroups([]); setLoading(false); setErrorMsg(null); return; }
    setLoading(true); setErrorMsg(null);
    const myReq = ++reqIdRef.current;
    const handle = setTimeout(async () => {
      try {
        const data: any = await api.get('/search', { q });
        if (myReq !== reqIdRef.current) return;
        setGroups(Array.isArray(data?.groups) ? data.groups : []);
        setActiveIndex(0);
      } catch {
        if (myReq !== reqIdRef.current) return;
        setGroups([]);
        setErrorMsg('Wyszukiwanie jest chwilowo niedostępne. Spróbuj ponownie.');
      } finally {
        if (myReq === reqIdRef.current) setLoading(false);
      }
    }, 220);
    return () => clearTimeout(handle);
  }, [query]);

  const go = useCallback((item: SearchItem) => {
    setOpen(false); setQuery(''); setGroups([]);
    inputRef.current?.blur();
    navigate(item.url);
  }, [navigate]);

  function onInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') { setOpen(false); setQuery(''); setGroups([]); inputRef.current?.blur(); return; }
    if (!flatItems.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => (i + 1) % flatItems.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => (i - 1 + flatItems.length) % flatItems.length); }
    else if (e.key === 'Enter') { e.preventDefault(); const it = flatItems[activeIndex] ?? flatItems[0]; if (it) go(it); }
  }

  const showDropdown = open && query.trim().length >= 2;
  const noResults = showDropdown && !loading && !errorMsg && flatItems.length === 0;
  let runningIndex = -1;

  return (
    <div ref={wrapRef} className="relative w-full max-w-md">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50 pointer-events-none" />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onInputKeyDown}
        placeholder="Szukaj zdarzeń, pojazdów, kierowców, formularzy…"
        className="w-full text-sm rounded-xl bg-white/10 border border-white/15 text-white placeholder:text-white/50 focus:border-[#2b8fe0] focus:bg-white/15 outline-none transition-colors"
        style={{ padding: '8px 64px 8px 36px' }}
        aria-label="Wyszukiwanie globalne"
      />
      <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center pointer-events-none">
        {loading
          ? <Loader2 className="w-4 h-4 animate-spin text-white/50" />
          : <kbd className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md text-white/60 bg-white/10 border border-white/15">⌘K</kbd>}
      </div>

      {showDropdown && (
        <div className="absolute left-0 mt-2 w-[min(28rem,90vw)] rounded-2xl bg-popover border border-border shadow-2xl z-[60] overflow-hidden flex flex-col" style={{ top: '100%', maxHeight: 460 }}>
          <div className="overflow-y-auto flex-1">
            {errorMsg ? (
              <div className="px-4 py-8 text-center">
                <AlertCircle className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                <p className="text-sm text-muted-foreground">{errorMsg}</p>
              </div>
            ) : noResults ? (
              <div className="px-4 py-8 text-center">
                <Search className="w-6 h-6 mx-auto mb-2 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">Brak wyników dla „<span className="font-semibold text-foreground">{query.trim()}</span>"</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Spróbuj numeru, nazwiska, rejestracji lub kodu.</p>
              </div>
            ) : (
              groups.map((g) => {
                const Vis = TYPE_VISUAL[g.type] ?? FALLBACK_VISUAL;
                return (
                  <div key={g.type}>
                    <div className="px-3 pt-3 pb-1 text-[10px] font-bold tracking-wider uppercase text-muted-foreground">{g.label}</div>
                    {g.items.map((item) => {
                      runningIndex += 1;
                      const idx = runningIndex;
                      const active = idx === activeIndex;
                      const Icon = Vis.icon;
                      return (
                        <button
                          key={item.id}
                          onMouseDown={(e) => { e.preventDefault(); go(item); }}
                          onMouseEnter={() => setActiveIndex(idx)}
                          className="w-full flex items-center gap-3 px-3 py-2 text-left transition-colors"
                          style={active ? { background: 'var(--accent)' } : undefined}
                        >
                          <span className="flex items-center justify-center rounded-lg flex-shrink-0" style={{ width: 32, height: 32, background: Vis.bg }}>
                            <Icon className="w-4 h-4" style={{ color: Vis.color }} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-foreground truncate">{item.title}</span>
                            {item.subtitle && <span className="block text-xs text-muted-foreground truncate">{item.subtitle}</span>}
                          </span>
                          {active && <CornerDownLeft className="w-3.5 h-3.5 flex-shrink-0 text-primary" />}
                        </button>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>
          {!noResults && !errorMsg && flatItems.length > 0 && (
            <div className="flex items-center gap-4 px-3 py-2 border-t border-border bg-muted/30 flex-shrink-0 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1"><ArrowDownUp className="w-3 h-3" /> nawigacja</span>
              <span className="flex items-center gap-1"><CornerDownLeft className="w-3 h-3" /> otwórz</span>
              <span className="ml-auto">esc — zamknij</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
